/**
 * DataState — the one place that encodes the stale-while-revalidate UX rule.
 *
 * THE RULE (the whole point of standardizing on TanStack Query):
 *   • `isPending` → we have NOTHING cached yet. Show a skeleton/spinner.
 *   • `isError` and no data → show the error with a Retry.
 *   • otherwise → RENDER THE DATA. Even while a background refetch is in
 *     flight (`isFetching`), never blank out data the user already had — show
 *     a quiet "Refreshing…" hint instead, and let the UI swap in fresh data
 *     when it lands.
 *
 * This is what makes navigating between pages feel instant: the second visit
 * paints cached data immediately and revalidates silently, rather than
 * flashing a spinner and refetching from scratch.
 *
 * Usage:
 *   const q = useQuery({ queryKey: qk.products(projectId), queryFn: ... });
 *   return (
 *     <DataState query={q}>
 *       {(products) => <ProductsTable rows={products} />}
 *     </DataState>
 *   );
 *
 * `query` accepts anything with the React-Query result shape; only the fields
 * below are read, so `useQuery`, `useInfiniteQuery`, etc. all work.
 */
import type { ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { LoadingPage } from '@/components/ui/loading-spinner';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@/lib/conflict';

interface QueryLike<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  refetch: () => void;
}

interface DataStateProps<T> {
  query: QueryLike<T>;
  /** Render the data once available. */
  children: (data: T) => ReactNode;
  /** Optional custom skeleton for the first (no-cache) load. */
  skeleton?: ReactNode;
  /** Hide the tiny top-right "Refreshing…" hint during background refetches. */
  hideRefreshingHint?: boolean;
}

export function DataState<T>({ query, children, skeleton, hideRefreshingHint }: DataStateProps<T>) {
  // First load — nothing cached. This is the ONLY time a full spinner is right.
  if (query.isPending) {
    return <>{skeleton ?? <LoadingPage />}</>;
  }

  // Hard error with no cached data to fall back to.
  if (query.isError && query.data === undefined) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <p className="text-muted-foreground text-sm">{apiErrorMessage(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  // We have data (possibly stale). Render it, plus a quiet background-refresh
  // hint so the user knows fresh data is on the way without losing what's there.
  return (
    <div className="relative">
      {!hideRefreshingHint && query.isFetching && <RefreshingHint />}
      {children(query.data as T)}
    </div>
  );
}

/** Subtle, non-blocking "we're revalidating" affordance. */
export function RefreshingHint() {
  return (
    <div className="text-muted-foreground bg-background/80 pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1 rounded-bl-md px-2 py-0.5 text-[11px] shadow-sm backdrop-blur-sm">
      <RefreshCw className="h-3 w-3 animate-spin" />
      Refreshing…
    </div>
  );
}
